import { CALENDAR_YEAR_MAX, CALENDAR_YEAR_MIN, LEAD_DAYS_LIST_MAX, LEAD_DAYS_MAX, LIMITS } from './constants';
import { CALENDAR_IDS, CalendarId, REPEAT_KINDS, type RepeatKind } from './enums';
import type { ImportantDateData } from './data-schemas';
import { isValidDate } from '../time/date';

/**
 * Important date records (data dictionary §3 `importantDate`): reading a stored record leniently
 * and checking the fields of a write strictly. Nothing here needs the lunar library: whether a
 * lunar month or day really exists is checked with `isValidCalendarDate` of `@wiztime/core/calendar`.
 */

/** The date of an important date: a calendar and year, month, day in it (same shape as the calendar entry's `CalendarDate`). */
export interface ImportantDateValue {
  readonly calendar: CalendarId;
  readonly year: number;
  readonly month: number;
  readonly day: number;
  /** Leap month (Chinese lunar only). */
  readonly leap: boolean;
}

/** An important date as this build understands it. */
export interface ImportantDateFields {
  readonly title: string;
  readonly date: ImportantDateValue;
  readonly repeat: RepeatKind;
  readonly leadDays: readonly number[];
  readonly note: string;
  readonly active: boolean;
}

/** Lead days as a list of distinct integers in [0, LEAD_DAYS_MAX], ascending; other values are dropped. */
export function normalizeLeadDays(days: readonly number[]): number[] {
  const valid = days.filter((d) => Number.isInteger(d) && d >= 0 && d <= LEAD_DAYS_MAX);
  return [...new Set(valid)].sort((a, b) => a - b);
}

const isCalendarId = (value: string): value is CalendarId =>
  (CALENDAR_IDS as readonly string[]).includes(value);
const isRepeatKind = (value: string): value is RepeatKind =>
  (REPEAT_KINDS as readonly string[]).includes(value);

/**
 * The fields of a stored important date, or null when this build cannot interpret the record
 * (a calendar or repeat kind of a newer client, a date that is not made of integers). Such a
 * record stays in storage untouched; it is not shown and does not remind.
 */
export function readImportantDate(data: ImportantDateData): ImportantDateFields | null {
  const { date } = data;
  if (!isCalendarId(date.calendar) || !isRepeatKind(data.repeat)) return null;
  if (!Number.isInteger(date.year) || !Number.isInteger(date.month) || !Number.isInteger(date.day))
    return null;
  return {
    title: data.title,
    date: { calendar: date.calendar, year: date.year, month: date.month, day: date.day, leap: date.leap },
    repeat: data.repeat,
    leadDays: normalizeLeadDays(data.leadDays),
    note: typeof data.note === 'string' ? data.note : '',
    active: data.active,
  };
}

/** The value fields of a write, before they are validated. */
export interface ImportantDateInputFields {
  readonly title: string;
  readonly date: ImportantDateValue;
  readonly repeat: RepeatKind;
  readonly leadDays: readonly number[];
  readonly note?: string | null;
}

/**
 * Problems of a write that need no calendar library (empty list = acceptable): title length,
 * a known calendar and repeat kind, integer year inside the supported range, month 1–12, day
 * 1–31, `leap` only in a calendar with leap months, a real Gregorian date, lead days within the
 * limits, note length. A lunar date that does not exist (day 30 of a 29-day month) is checked
 * by the caller with `isValidCalendarDate`.
 */
export function importantDateIssues(input: ImportantDateInputFields): string[] {
  const issues: string[] = [];
  const title = input.title.trim();
  if (title.length < 1 || title.length > LIMITS.importantDateTitleMax) {
    issues.push(`title: expected 1-${LIMITS.importantDateTitleMax} characters`);
  }
  const { date } = input;
  if (!isCalendarId(date.calendar)) issues.push('date.calendar: unknown calendar');
  if (!isRepeatKind(input.repeat)) issues.push('repeat: unknown repeat kind');
  if (!Number.isInteger(date.year) || date.year < CALENDAR_YEAR_MIN || date.year > CALENDAR_YEAR_MAX) {
    issues.push(`date.year: expected an integer in ${CALENDAR_YEAR_MIN}-${CALENDAR_YEAR_MAX}`);
  }
  if (!Number.isInteger(date.month) || date.month < 1 || date.month > 12)
    issues.push('date.month: expected 1-12');
  if (!Number.isInteger(date.day) || date.day < 1 || date.day > 31) issues.push('date.day: expected 1-31');
  if (date.leap && date.calendar !== CalendarId.ChineseLunar)
    issues.push('date.leap: only the lunar calendar has leap months');
  if (
    issues.length === 0 &&
    date.calendar === CalendarId.Gregorian &&
    !isValidDate(
      `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`,
    )
  ) {
    issues.push('date: not a real Gregorian date');
  }
  const normalized = normalizeLeadDays(input.leadDays);
  if (normalized.length !== input.leadDays.length) {
    issues.push(`leadDays: expected distinct integers in 0-${LEAD_DAYS_MAX}`);
  }
  if (normalized.length > LEAD_DAYS_LIST_MAX) issues.push(`leadDays: at most ${LEAD_DAYS_LIST_MAX} entries`);
  if ((input.note ?? '').length > LIMITS.importantDateNoteMax) {
    issues.push(`note: at most ${LIMITS.importantDateNoteMax} characters`);
  }
  return issues;
}
